/**
 * Utilities for computing container effect overlay classes and inline styles.
 *
 * The overlay <div> is rendered as a sibling layer inside the container with
 * `pointer-events: none` so all content underneath remains fully interactive.
 */

const ANIMATABLE_EFFECTS = new Set(["prism", "mesh", "streak", "glow"]);

const EFFECT_CLASS_MAP = {
  glass: "ce-glass",
  frost: "ce-frost",
  crackle: "ce-crackle",
  emboss: "ce-emboss",
  etch: "ce-etch",
  holographic: "ce-holographic",
  noise: "ce-noise",
  prism: "ce-prism",
  ridge: "ce-ridge",
  silk: "ce-silk",
  mesh: "ce-mesh",
  vignette: "ce-vignette",
  scanlines: "ce-scanlines",
  halftone: "ce-halftone",
  streak: "ce-streak",
  sunken: "ce-sunken",
  glow: "ce-glow",
  brushed: "ce-brushed",
  shadow: "ce-shadow",
};

/**
 * Build a CSS gradient string for a single shadow direction.
 * Each gradient fades from a dark edge into transparency at ~55% of the container.
 */
const SHADOW_GRADIENT_BUILDERS = {
  top: (a) => `linear-gradient(to bottom, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  bottom: (a) => `linear-gradient(to top, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  left: (a) => `linear-gradient(to right, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  right: (a) => `linear-gradient(to left, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  "top-left": (a) => `linear-gradient(to bottom right, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  "top-right": (a) => `linear-gradient(to bottom left, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  "bottom-left": (a) => `linear-gradient(to top right, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  "bottom-right": (a) => `linear-gradient(to top left, rgba(0,0,0,${a}) 0%, transparent 55%)`,
  center: (a) => `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,${a}) 100%)`,
};

const DEFAULT_SHADOW_DIRECTION = "bottom";

/**
 * Compute the inline `background-image` for the partial-shadow effect based on
 * the user's chosen direction(s). Returns null when no direction yields output
 * so the CSS class default can take over.
 */
function buildShadowBackgroundImage(shadowDirection, intensity) {
  const dirs = (shadowDirection || DEFAULT_SHADOW_DIRECTION)
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);

  const opacity = Math.max(0, Math.min(1, (intensity / 100) * 0.65));
  const opacityStr = opacity.toFixed(3);

  const gradients = dirs
    .map((d) => SHADOW_GRADIENT_BUILDERS[d])
    .filter(Boolean)
    .map((fn) => fn(opacityStr));

  return gradients.length > 0 ? gradients.join(", ") : null;
}

/**
 * Build the CSS class string and inline style object for a container effect overlay.
 *
 * @param {Object|null} containerEffect - The container_effect data from the API
 * @returns {{ className: string, style: Object, active: boolean }}
 */
export function getContainerEffectProps(containerEffect) {
  if (!containerEffect || !containerEffect.effect || containerEffect.effect === "none") {
    return { className: "", style: {}, active: false };
  }

  const {
    effect,
    intensity = 50,
    color_tint,
    blur_amount = 12,
    animated,
    shadow_direction,
  } = containerEffect;
  const baseClass = EFFECT_CLASS_MAP[effect];
  if (!baseClass) {
    return { className: "", style: {}, active: false };
  }

  const classes = [baseClass];
  if (animated && ANIMATABLE_EFFECTS.has(effect)) classes.push("ce-animated");

  const style = {
    "--ce-intensity": intensity,
    "--ce-blur": `${blur_amount}px`,
  };

  if (effect === "shadow") {
    const bgImage = buildShadowBackgroundImage(shadow_direction, intensity);
    if (bgImage) {
      style.backgroundImage = bgImage;
    }
  }

  if (color_tint) {
    style.backgroundColor = color_tint;
    style.mixBlendMode = "soft-light";
  }

  return {
    className: classes.join(" "),
    style,
    active: true,
  };
}

/**
 * All available container effect options for the editor picker.
 */
export const CONTAINER_EFFECT_OPTIONS = [
  { value: "none", label: "None", description: "No effect applied" },
  { value: "glass", label: "Glass", description: "Frosted glassmorphism with backdrop blur" },
  { value: "frost", label: "Frost", description: "Heavy frosted overlay with soft saturation" },
  { value: "crackle", label: "Crackle", description: "Gritty noise texture for a raw, tactile feel" },
  { value: "emboss", label: "Emboss", description: "3D raised surface with light and shadow" },
  { value: "etch", label: "Etch", description: "Subtle engraved / letterpress depth" },
  { value: "holographic", label: "Holographic", description: "Prismatic rainbow shimmer overlay" },
  { value: "noise", label: "Noise", description: "Film grain texture for analog warmth" },
  { value: "prism", label: "Prism", description: "Conic rainbow light refraction" },
  { value: "ridge", label: "Ridge", description: "Layered edge depth with highlight and shadow" },
  { value: "silk", label: "Silk", description: "Smooth satin sheen reflection" },
  { value: "mesh", label: "Mesh", description: "Vibrant gradient mesh overlay" },
  { value: "vignette", label: "Vignette", description: "Cinematic darkened edge fade" },
  { value: "scanlines", label: "Scanlines", description: "Retro CRT horizontal line pattern" },
  { value: "halftone", label: "Halftone", description: "Print-style Ben-Day dot pattern" },
  { value: "streak", label: "Streak", description: "Diagonal light flare sweep" },
  { value: "sunken", label: "Sunken", description: "Recessed depth like sitting in a depression" },
  { value: "glow", label: "Glow", description: "Soft luminous ambient aura from edges" },
  { value: "brushed", label: "Brushed", description: "Brushed metal texture with directional grain" },
  { value: "shadow", label: "Shadow", description: "Partial directional shadow on chosen edge(s)" },
];
